/**
 * Line-art icons + garment glyphs, ported verbatim from the redesign mockup. All are
 * `currentColor` strokes so they inherit the surrounding text colour. Kept as small inline
 * SVGs (no icon dependency) to match the mockup 1:1.
 */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>
const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

/* ---- UI icons ---- */
export const IconSearch = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)
export const IconCamera = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
)
export const IconPlus = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2.2} {...base} {...p}>
    <path d="M12 5v14M5 12h14" />
  </svg>
)
export const IconX = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
)
export const IconChevronDown = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="m6 9 6 6 6-6" />
  </svg>
)
export const IconMenu = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
)
export const IconGlobe = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
  </svg>
)
export const IconLogout = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
)
export const IconArrowRight = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)
export const IconArrowLeft = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </svg>
)
export const IconGift = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <rect x="3" y="8" width="18" height="4" />
    <path d="M12 8v13M5 12v9h14v-9" />
    <path d="M12 8S11 3 8 3a2.5 2.5 0 0 0 0 5zM12 8s1-5 4-5a2.5 2.5 0 0 1 0 5z" />
  </svg>
)
export const IconCheck = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2.4} {...base} {...p}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)
export const IconCopy = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
)
export const IconInfo = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
)
export const IconBox = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <path d="m21 8-9-5-9 5 9 5 9-5Z" />
    <path d="M3 8v8l9 5 9-5V8M12 13v8" />
  </svg>
)
export const IconPin = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.8} {...base} {...p}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)
export const IconWarn = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
)
export const IconChevronLeft = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2.2} {...base} {...p}>
    <path d="m15 6-6 6 6 6" />
  </svg>
)
export const IconChevronRight = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2.2} {...base} {...p}>
    <path d="m9 6 6 6-6 6" />
  </svg>
)
export const IconVolumeMute = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M11 5 6 9H2v6h4l5 4z" />
    <path d="m23 9-6 6M17 9l6 6" />
  </svg>
)
export const IconVolume = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M11 5 6 9H2v6h4l5 4z" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
)
export const IconLogo = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={2} {...base} {...p}>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
)

/* ---- garment glyphs (used as placeholder thumbnails when a product has no image) ---- */
export const GlyphBag = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M5 8h14l-1 12H6L5 8Z" />
    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
  </svg>
)
export const GlyphShirt = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M8 3 3 6l2 4 2-1v9h10V9l2 1 2-4-5-3-4 3-4-3Z" />
    <path d="M12 6v3" />
  </svg>
)
export const GlyphCoat = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M8 3 4 6l1.5 3L7 8v12h10V8l1.5 1L20 6l-4-3-4 3-4-3Z" />
    <path d="M12 6v14" />
  </svg>
)
export const GlyphKnit = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M8 3 3 7l2 3 2-1v9h10V9l2 1 2-3-5-4-4 3-4-3Z" />
    <path d="M9 12l6 4M15 12l-6 4" />
  </svg>
)
export const GlyphJacket = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M7 3 3 6l2 3 2-1v11h10V8l2 1 2-3-4-3-2 4-2-4-2 4-2-4Z" />
  </svg>
)
export const GlyphScarf = (p: IconProps) => (
  <svg viewBox="0 0 24 24" strokeWidth={1.6} {...base} {...p}>
    <path d="M6 4c4 3 8 3 12 0v6c-4 3-8 3-12 0V4Z" />
    <path d="M9 10v9M11 10v10" />
  </svg>
)

/** Garment-glyph lookup by key (mockup `G[...]`), for placeholder product thumbnails. */
export const GLYPHS: Record<string, (p: IconProps) => JSX.Element> = {
  coat: GlyphCoat,
  knit: GlyphKnit,
  shirt: GlyphShirt,
  jacket: GlyphJacket,
  scarf: GlyphScarf,
  bag: GlyphBag,
}

export function Glyph({ name, ...p }: IconProps & { name: string }) {
  const G = GLYPHS[name] ?? GlyphShirt
  return <G {...p} />
}
